import type { ReactElement } from "react";
import { useCallback } from "react";

import { isEmpty } from "lodash";

import type { IDropdownButtonProps, IEllipsisDropdownItemProps, IDropdownChild } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import DropdownLabelWithCount from "app/pages/sales-intelligence/common-components/dropdown/DropdownLabelWithCount/DropdownLabelWithCount";
import { StyledTruncatedInnerText } from "app/pages/sales-intelligence/common-components/dropdown/DropdownLabelWithCount/styles";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type { SignalFilterDropdownLabelModel } from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFilterDropdownLabelModel";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import { searchDropdownItems } from "app/pages/sales-intelligence/utils/dropdowns";
import type { Translatable } from "app/pages/sales-intelligence/types";
import type { ICountryObject } from "app/services/CountryService";

import useCountriesWithoutWW from "../../../hooks/useCountriesWithoutWW";
import useSignalFilterDropdownLabel from "../../../hooks/useSignalFilterDropdownLabel";
import SignalFilterDropdownLabel from "../shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import useMultiselectHandler from "../../../hooks/useMultiselectHandler";
import type { FilterDropdownCountryItemProps } from "../shared/FilterDropdown/FilterDropdownCountryItem/FilterDropdownCountryItem";
import FilterDropdownCountryItem from "../shared/FilterDropdown/FilterDropdownCountryItem/FilterDropdownCountryItem";
import FilterSelectAllItem from "../shared/FilterDropdown/FilterSelectAllItem/FilterSelectAllItem";
import FilterMultiselectDropdown from "../shared/FilterMultiselectDropdown/FilterMultiselectDropdown";

import FilterButtonLabelWithCountryIcon from "./FilterButtonLabelWithCountryIcon/FilterButtonLabelWithCountryIcon";

type CountriesFilterButtonLabelParams = {
    withAllOptionsCount: boolean;
    isFullSelection: boolean;
    validCountriesLength: number;
    filterNameTranslatable: Readonly<Translatable>;
    filterLabelModel: SignalFilterDropdownLabelModel;
};

function renderCountriesFilterButtonLabel({
    withAllOptionsCount,
    isFullSelection,
    validCountriesLength,
    filterNameTranslatable,
    filterLabelModel,
}: CountriesFilterButtonLabelParams): ReactElement {
    if (!withAllOptionsCount && isFullSelection) {
        return (
            <I18n
                translatable={filterNameTranslatable}
                element={StyledTruncatedInnerText}
            />
        );
    }

    if (withAllOptionsCount && isFullSelection) {
        return (
            <DropdownLabelWithCount
                isFullSelection={isFullSelection}
                text={filterNameTranslatable}
                count={validCountriesLength}
            />
        );
    }

    return <SignalFilterDropdownLabel model={filterLabelModel} />;
}

type DropdownButtonProps = Pick<IDropdownButtonProps, "isOpen" | "disabled">;

type FilterValue = readonly number[] | undefined;

export type SignalCountriesFilterProps = {
    filterKey: string;
    isActive?: boolean;
    isAutoOpen?: boolean;
    preContentComponent?: ReactElement;
    renderDropdownItems?: (
        dropDownButton: ReactElement,
        selectedAllItem: IDropdownChild,
        items: readonly IDropdownChild[],
    ) => IDropdownChild[];
    selectedValue: FilterValue;
    options: readonly ICountryObject[];
    withAllOptionsCount?: boolean;
    hasSearch?: boolean;
    renderOptions?(options: readonly ICountryObject[]): ReactElement[];
    onValueChange(filterValue: FilterValue): void;
    onToggle?(isOpen: boolean): void;
};

const SignalMultiselectCountriesFilter = (props: SignalCountriesFilterProps) => {
    const {
        filterKey,
        isActive = true,
        isAutoOpen = false,
        preContentComponent = null,
        selectedValue,
        options,
        withAllOptionsCount = true,
        hasSearch = true,
        renderDropdownItems,
        renderOptions,
        onValueChange,
        onToggle,
    } = props;

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, isAutoOpen);

    const validCountries = useCountriesWithoutWW(options);

    const isFullSelection = selectedValue?.length === validCountries.length;

    const { onSelect } = useMultiselectHandler({
        selectedValue,
        onValueChange,
    });

    const buildFilterName = useCallback(() => {
        const nameBuilder = filterService.getFilterName(FilterType.Countries);

        return nameBuilder.buildName({ countries: selectedValue ?? [] }, options, {
            i18nKeyModifier: filterKey,
        });
    }, [options, selectedValue, filterKey]);

    const { model: filterLabelModel, filterName } = useSignalFilterDropdownLabel({
        buildName: buildFilterName,
    });

    const handleChange = useCallback(
        (value: number | null, isSelected: boolean) => {
            if (value === null) {
                const values = isSelected ? [] : validCountries.map(({ id }) => id);

                onValueChange(values);
            } else {
                onSelect(value, isSelected);
            }
        },
        [onSelect, onValueChange, validCountries],
    );

    const renderButton = ({ isOpen, disabled }: DropdownButtonProps) => {
        const comparableSelectedValue = selectedValue ?? [];

        const isEmptySelection = isEmpty(selectedValue);

        return (
            <SignalFilterDropdownButton
                id={filterKey}
                isOpen={isOpen}
                disabled={disabled}
                hasValue={!isEmptySelection}
            >
                <FilterButtonLabelWithCountryIcon
                    showIcon={!isFullSelection}
                    countryCode={comparableSelectedValue[0]}
                >
                    {renderCountriesFilterButtonLabel({
                        withAllOptionsCount,
                        isFullSelection,
                        validCountriesLength: validCountries.length,
                        filterNameTranslatable: filterName.translatable,
                        filterLabelModel,
                    })}
                </FilterButtonLabelWithCountryIcon>
            </SignalFilterDropdownButton>
        );
    };

    const renderItem = (props: Readonly<FilterDropdownCountryItemProps>) => {
        return (
            <FilterDropdownCountryItem
                key={props.id}
                {...props}
            />
        );
    };

    const renderSelectAllItem = (props: IEllipsisDropdownItemProps) => {
        return (
            <FilterSelectAllItem {...props}>
                {translate(`si.signals.filters.${filterKey}.name.any`, {
                    count: validCountries.length,
                })}
            </FilterSelectAllItem>
        );
    };

    return (
        <FilterMultiselectDropdown
            ref={dropdownRef}
            filterKey={filterKey}
            selectedOptions={selectedValue ?? []}
            isFullSelection={isFullSelection}
            preContentComponent={preContentComponent}
            searchFilter={searchDropdownItems}
            buttonComponent={renderButton}
            selectAllComponent={renderSelectAllItem}
            renderDropdownItems={renderDropdownItems}
            disabled={!isActive}
            hasSearch={hasSearch}
            onChange={handleChange}
            onToggle={onToggle}
        >
            {typeof renderOptions === "undefined" ? validCountries.map(renderItem) : renderOptions(validCountries)}
        </FilterMultiselectDropdown>
    );
};

export default SignalMultiselectCountriesFilter;
